/**
 * Date Range Selector Component
 * Allows selecting date range for reports
 */

import { Calendar, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '@/i18n';
import { formatDateInputValue, getRelativeDateRange } from '@/features/reports/dateRange';

interface DateRangeSelectorProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  className?: string;
}

export function DateRangeSelector({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRefresh,
  isRefreshing,
  className,
}: DateRangeSelectorProps) {
  const { t } = useI18n();
  const handleQuickSelect = (dayCount: number) => {
    const range = getRelativeDateRange(dayCount);
    onStartDateChange(range.startDate);
    onEndDateChange(range.endDate);
  };
  const referenceDate = new Date();
  const today = formatDateInputValue(referenceDate);

  const quickRanges = [
    { dayCount: 7, label: t('reportsPage.dateRangeSelector.last7Days') },
    { dayCount: 30, label: t('reportsPage.dateRangeSelector.last30Days') },
    { dayCount: 90, label: t('reportsPage.dateRangeSelector.last90Days') },
    { dayCount: 365, label: t('reportsPage.dateRangeSelector.last1Year') },
  ];

  return (
    <div className={clsx('flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center', className)}>
      <div className="flex flex-wrap items-center gap-2">
        {quickRanges.map((range) => {
          const expected = getRelativeDateRange(range.dayCount, referenceDate);
          const isActive = endDate === today && startDate === expected.startDate && endDate === expected.endDate;
          return (
            <button
              key={range.dayCount}
              type="button"
              onClick={() => handleQuickSelect(range.dayCount)}
              aria-pressed={isActive}
              className={clsx(
                'px-3 py-1.5 text-sm rounded-lg transition-colors',
                isActive
                  ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
              )}
            >
              {range.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="w-[10.5rem] pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <span className="text-sm text-gray-500">{t('reportsPage.dateRangeSelector.to')}</span>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            className="w-[10.5rem] pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              isRefreshing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
            )}
          >
            <RefreshCw
              className={clsx('w-4 h-4', isRefreshing && 'animate-spin')}
            />
          </button>
        )}
      </div>
    </div>
  );
}
